import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  MessageSquare,
  UserRound,
  Users,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useDailyScrum } from '../../features/sprintboard/useDailyScrum';
import type { ScrumTaskEntry } from '../../features/sprintboard/scrum/dailyScrumModel';

/** One task line: title, Completed / Next, status, ⚠ Blocked. */
function ScrumEntry({
  entry,
  onOpen,
}: {
  entry: ScrumTaskEntry;
  onOpen: (taskId: string) => void;
}) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(entry.taskId)}
          className="text-left text-sm font-medium text-zinc-100 hover:text-white"
        >
          {entry.taskTitle}
        </button>
        {entry.blocked && (
          <Badge tone="danger" dot>
            ⚠ Blocked
          </Badge>
        )}
      </div>
      <dl className="mt-1.5 space-y-1 text-xs">
        {entry.completed && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-emerald-400">Completed</dt>
            <dd className="text-zinc-300">{entry.completed}</dd>
          </div>
        )}
        {entry.next && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-sky-400">Next</dt>
            <dd className="text-zinc-300">{entry.next}</dd>
          </div>
        )}
        {entry.blocker && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-red-400">Blocker</dt>
            <dd className="text-zinc-300">{entry.blocker}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/**
 * Daily Scrum (§16) — the standup roll-up for the active room: task updates
 * auto-grouped by engineer → task, each showing Completed / Next / Status and a
 * ⚠ Blocked flag. It's a pure projection of task updates ("+ Add Update" on a
 * task), so one update powers both the task and this view.
 */
export function DailyScrumPage() {
  const navigate = useNavigate();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { groups, updates, loading, error } = useDailyScrum();

  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<MessageSquare className="h-5 w-5" />}
          title="Daily Scrum"
          description="Standup updates grouped by engineer."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Daily Scrum rolls up a room's task updates. Select or create a room first."
          action={<Button onClick={() => navigate('/rooms')}>Go to Rooms</Button>}
        />
      </div>
    );
  }

  const openTask = (taskId: string) => navigate(`/tasks/${taskId}`);

  return (
    <div>
      <PageHeader
        icon={<MessageSquare className="h-5 w-5" />}
        title="Daily Scrum"
        description={
          <span className="inline-flex items-center gap-1.5">
            Latest updates in
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading daily scrum" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the daily scrum"
          description={error}
        />
      ) : updates.length === 0 ? (
        <EmptyState
          icon={<MessageSquare className="h-6 w-6" />}
          title="No updates yet"
          description="Open a task and use “+ Add Update” to post what you completed and what's next — it shows up here automatically."
        />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.engineer}>
              <CardBody className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
                    <UserRound className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold text-zinc-100">
                    {group.engineer}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-3 px-1.5 text-xs text-zinc-500">
                    <Users className="h-3 w-3" aria-hidden="true" />
                    {group.entries.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.entries.map((entry) => (
                    <ScrumEntry
                      key={`${group.engineer}-${entry.taskId}`}
                      entry={entry}
                      onOpen={openTask}
                    />
                  ))}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default DailyScrumPage;
